import type { ActivityActionItem } from '@/types/activity'
import type { ClockingSlot, ClockingTimeEntry } from './types'

const FINALIZED_CLOCKING_STATUSES = new Set([
  'accepted',
  'ready2release',
  'releasing',
  'released',
])

export const getClockingTimeEntryGroupId = (slot: ClockingSlot) =>
  String(slot.timeentry_data?.id ?? slot.timeentry ?? slot.id)

export const getClockingTimeEntryStartTime = (slots: ClockingSlot[]) => {
  const start = slots.find(slot => slot.timeentry_data?.start)?.timeentry_data
    ?.start
  const fallbackStart = slots.find(slot => slot.start)?.start
  const timestamp = new Date(start ?? fallbackStart ?? 0).getTime()

  return Number.isNaN(timestamp) ? 0 : timestamp
}

export const isClockingSlot = (slot: ClockingSlot) =>
  slot.non_svc_source === 'Clocking' || slot.apps?.includes('Clocking')

export const isRunningClockingSlot = (slot: ClockingSlot) =>
  isClockingSlot(slot) && !slot.stop

export const buildClockingDialogSlot = (
  slot: ActivityActionItem
): ClockingSlot | null => {
  const entrySource =
    slot.relateTimeEntry ??
    slot.timeentry_data ??
    slot.timeEntry ??
    slot.time_entry ??
    slot
  const timeEntryId =
    slot.timeentry ?? slot.timeentry_id ?? slot.time_entry_id ?? entrySource.id

  if (timeEntryId == null) return null

  return {
    ...slot,
    id: slot.id,
    start: slot.start ? String(slot.start) : null,
    stop: slot.stop ? String(slot.stop) : null,
    timeentry: timeEntryId,
    timeentry_data: {
      ...(entrySource as ClockingTimeEntry),
      id: timeEntryId,
    },
  }
}

export const isFinalizedClockingSlot = (slot: ClockingSlot) => {
  const timeEntry = slot.timeentry_data
  const status = String(timeEntry?.status ?? slot.status ?? '').toLowerCase()
  const subStatus = String(
    timeEntry?.sub_status ?? slot.sub_status ?? ''
  ).toLowerCase()

  return (
    Boolean(timeEntry?.approved_on ?? slot.approved_on) ||
    FINALIZED_CLOCKING_STATUSES.has(status) ||
    subStatus === 'ready_to_release'
  )
}

export const isAcceptedClockingSlot = isFinalizedClockingSlot

export const formatClockingDuration = (
  start?: string | null,
  stop?: string | null,
  now = Date.now()
) => {
  if (!start) return '00:00:00'

  const startTime = new Date(start).getTime()
  const stopTime = stop ? new Date(stop).getTime() : now
  const totalSeconds = Math.max(0, Math.floor((stopTime - startTime) / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return [hours, minutes, seconds]
    .map(value => String(value).padStart(2, '0'))
    .join(':')
}

export const formatClockingSlotsDuration = (
  slots: ClockingSlot[],
  now = Date.now()
) => {
  const totalSeconds = slots.reduce((total, slot) => {
    if (!slot.start) return total
    const startTime = new Date(slot.start).getTime()
    const stopTime = slot.stop ? new Date(slot.stop).getTime() : now
    return total + Math.max(0, Math.floor((stopTime - startTime) / 1000))
  }, 0)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  return [hours, minutes, seconds]
    .map(value => String(value).padStart(2, '0'))
    .join(':')
}
